import { Logger } from 'src/utils/Logger';
import { create } from 'zustand';
import { mutative } from 'zustand-mutative';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { UpsertEntityInput } from '../merge-review/useUpsertEntities';
import { Detection, EntityFields, FieldTypeMetadata, FieldValue } from '../types';
import { EntityMetadata } from 'src/UI/dynamicUI/types';
import extractCategoriesForDetection from './helpers/extractCategoriesForDetection';
import { filterInsertedEntityConflicts } from './helpers/filterInsertedEntityConflicts';
import { getUpdatedDetectionState, addToCategories } from './helpers/updateDetectionState';
import * as Sentry from '@sentry/react';
import { idbStorage } from 'src/common/stores/idbStorage';
import { camelCaseOrSnakeCaseToWords, fieldsToHide } from '../merge-review/ReviewHelperFunctions';
import { isEmpty } from './helpers/isEmpty';
import { entityFieldsToFilterOut } from '../detected-entities/hooks/DetectionActionMutations';

type CountMetadata = {
  total: number;
  actioned: number;
  resolved: number;
  inserts: number;
  updates: number;
  discarded: number;
  active: number;
};

type CategoryCountMetadata = {
  [entityType: string]: CountMetadata;
};

type AllCountMetadata = {
  overall: CountMetadata;
  byCategory: {
    [K in EntityCategory]: CategoryCountMetadata;
  };
};

type FileState = {
  expiresAt: number; // Timestamp when the local file state expires
  categories: Categories;
  entityTypeByDetectionId: Record<Detection['id'], string>;
  countMetadata: AllCountMetadata;
  insertedEntityIds: string[];
};

/**
 * new: No conflicts (renders as group if isConflictable)
 * conflicts: Conflicts & isConflictable (always renders as group)
 * info-update: Conflicts & !isConflictable (never renders as group)
 */
export type EntityCategory = 'new' | 'conflicts' | 'info-update';

export type Categories = {
  [K in EntityCategory]: CategoryData;
};

type CategoryData = {
  [entityType: string]: {
    [detectionId: string]: DetectionState;
  };
};

export type DetectionState = {
  id: Detection['id'];
  fieldsByKey: Record<string, Field>;
  metadata: EntityMetadata;
  entityType: string;
  entityLabel: string;
  entityId: string | null;
  conflicts: ConflictState[];
  selectedConflictIndex?: number;
  isResolved: boolean;
  isDiscarded: boolean;
  isActioning: boolean;
  isConflictable: boolean;
  lastAction: LastAction | null;
};

export type ConflictState = {
  id: string;
  conflictingEntityFields: EntityFields;
  conflictingFields: string[];
};

export type Field = {
  key: string;
  value: FieldValue;
  metadata: FieldTypeMetadata;
  isReadOnly: boolean;
  isSelected: boolean;
  isResolved: boolean;
  isDiscarded: boolean;
  isActioning: boolean;
  isLocallyEdited?: boolean;
};

type InsertAction = {
  type: 'insert';
  targetEntityFieldsMetadata: EntityMetadata;
  targetEntityType: string;
  insertedEntityId: string;
};

type UpdateAction = {
  type: 'update';
  targetEntityFieldsMetadata: EntityMetadata;
  targetEntityType: string;
  previousValues: EntityFields;
};

type LastAction = InsertAction | UpdateAction;

type State = {
  appliedResetIdentifiers: string[]; // Single-use tokens to remove the state of a file
  files: Record<string, FileState>;
  fileId: string | null;
};

type HandleDetectionDataOptions = {
  excludeInsertedEntityConflicts?: boolean;
};

type Actions = {
  clearExpiredFiles: () => void;
  handleDetectionData: (
    fileId: string,
    detections?: Detection[],
    showNonConflictingFields?: boolean,
    options?: HandleDetectionDataOptions,
  ) => void;
  clearFileData: (fileId: string, resetIdentifier: string) => void;
  saveLocalFieldValue: (
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName: string,
    value: FieldValue,
  ) => void;
  selectConflictIndex: (
    category: EntityCategory,
    detectionId: Detection['id'],
    conflictIndex: number,
  ) => void;
  setDiscarded: (
    isDiscarded: boolean,
    category: EntityCategory,
    detectionId: Detection['id'],
    options?: {
      fieldNames?: string[];
      skipCountUpdate?: boolean;
    },
  ) => void;
  discardAll: (category: EntityCategory, entityType: string) => void;
  setResolved: (
    isResolved: boolean,
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
  ) => void;
  setFileId: (fileId: string) => void;
  setCategoryResolved: (isResolved: boolean, category: EntityCategory) => void;
  setEntitiesOfTypeResolved: (
    isResolved: boolean,
    category: EntityCategory,
    entityType: string,
  ) => void;
  toggleConflictField: (
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName: string,
  ) => void;
  setExistingValues: (
    category: EntityCategory,
    detectionId: Detection['id'],
    values: EntityFields,
  ) => void;
  setLastAction: (
    category: EntityCategory,
    detectionId: Detection['id'],
    action: LastAction | null,
    fieldName?: string,
  ) => void;
  setCategoryInsertLastActions: (
    category: EntityCategory,
    insertedIds: {
      [detectionId: string]: string;
    },
  ) => void;
  setEntityOfTypeInsertLastAction: (
    category: EntityCategory,
    entityType: string,
    insertedIds: {
      [detectionId: string]: string;
    },
  ) => void;
  setEntityIdOnDetection: (
    category: EntityCategory,
    detectionId: Detection['id'],
    entityId: string | null,
  ) => void;
  setActioning: (
    isActioning: boolean,
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
  ) => void;
  setCategoryActioning: (isActioning: boolean, category: EntityCategory) => void;
  setEntitiesOfTypeActioning: (
    isActioning: boolean,
    category: EntityCategory,
    entityType: string,
  ) => void;
};

const calculateDetectionCounts = (
  fields: Field[],
  detectionState: DetectionState,
  category: EntityCategory,
): CountMetadata => {
  if (detectionState.isConflictable) {
    const isResolved = detectionState.isResolved;
    const isDiscarded = detectionState.isDiscarded;
    const isActioned = isResolved || isDiscarded;

    return {
      total: 1,
      resolved: isResolved ? 1 : 0,
      inserts: detectionState.lastAction?.type === 'insert' ? 1 : 0,
      updates: detectionState.lastAction?.type === 'update' ? 1 : 0,
      discarded: isDiscarded ? 1 : 0,
      active: isActioned ? 0 : 1,
      actioned: isActioned ? 1 : 0,
    };
  }

  let resolved = 0;
  let discarded = 0;

  fields.forEach(field => {
    if (field.isResolved) {
      resolved++;
    } else if (field.isDiscarded) {
      discarded++;
    }
  });

  const total = fields.length;
  const actioned = resolved + discarded;
  const active = total - actioned;

  return {
    total,
    resolved,
    inserts: category === 'new' ? resolved : 0,
    updates: category === 'info-update' ? resolved : 0,
    discarded,
    active,
    actioned,
  };
};

const recalculateAllCounts = (fileState: FileState): AllCountMetadata => {
  const result: AllCountMetadata = {
    overall: {
      total: 0,
      actioned: 0,
      resolved: 0,
      inserts: 0,
      updates: 0,
      discarded: 0,
      active: 0,
    },
    byCategory: {
      new: {},
      conflicts: {},
      'info-update': {},
    },
  };

  Object.keys(fileState.categories).forEach(categoryKey => {
    const category = categoryKey as EntityCategory;
    const categoryData = fileState.categories[category];

    Object.keys(categoryData).forEach(entityType => {
      const entityData = categoryData[entityType];
      const entityCounts = {
        total: 0,
        actioned: 0,
        resolved: 0,
        inserts: 0,
        updates: 0,
        discarded: 0,
        active: 0,
      };

      Object.keys(entityData).forEach(detectionId => {
        const detection = entityData[detectionId];
        const fields = Object.values(detection.fieldsByKey);

        if (detection) {
          const counts = calculateDetectionCounts(fields, detection, category);
          entityCounts.total += counts.total;
          entityCounts.actioned += counts.actioned;
          entityCounts.resolved += counts.resolved;
          entityCounts.inserts += counts.inserts;
          entityCounts.updates += counts.updates;
          entityCounts.discarded += counts.discarded;
          entityCounts.active += counts.active;
        }
      });

      result.byCategory[category][entityType] = entityCounts;

      result.overall.total += entityCounts.total;
      result.overall.actioned += entityCounts.actioned;
      result.overall.inserts += entityCounts.inserts;
      result.overall.updates += entityCounts.updates;
      result.overall.resolved += entityCounts.resolved;
      result.overall.discarded += entityCounts.discarded;
      result.overall.active += entityCounts.active;
    });
  });

  return result;
};

const initialState: State = {
  appliedResetIdentifiers: [],
  files: {},
  fileId: null,
};

export const useDetectedEntitiesStore = create<State & Actions>()(
  devtools(
    persist(
      mutative((set, get) => ({
        ...initialState,

        // Actions
        clearExpiredFiles: () => {
          set(
            state => {
              const now = Date.now();
              const currentFileId = state.fileId;
              Object.keys(state.files).forEach(fileId => {
                const fileState = state.files[fileId];
                if (fileState.expiresAt < now && fileId !== currentFileId) {
                  delete state.files[fileId];
                }
              });
            },
            undefined,
            'detected-entities/clearExpiredFiles',
          );

          Sentry.addBreadcrumb({
            category: 'detected-entities',
            message: 'clearExpiredFiles',
            level: 'info',
            data: {
              numRemainingFiles: Object.keys(get().files).length,
            },
          });
        },

        handleDetectionData: (
          fileId: string,
          detections?: Detection[],
          showNonConflictingFields = false,
          options?: HandleDetectionDataOptions,
        ) => {
          if (detections == null) {
            return;
          }

          const state = get();
          Sentry.addBreadcrumb({
            category: 'detected-entities',
            message: 'handleDetectionData',
            level: 'info',
            data: {
              fileId,
              numDetections: detections.length,
              fileAlreadyStored: state.files[fileId] != null,
              totalNumFiles: Object.keys(state.files).length,
            },
          });

          set(
            state => {
              if (state.files[fileId] == null) {
                state.files[fileId] = {
                  expiresAt: 0,
                  entityTypeByDetectionId: {},
                  categories: {
                    new: {},
                    conflicts: {},
                    'info-update': {},
                  },
                  countMetadata: {
                    overall: {
                      total: 0,
                      actioned: 0,
                      resolved: 0,
                      inserts: 0,
                      updates: 0,
                      discarded: 0,
                      active: 0,
                    },
                    byCategory: {
                      new: {},
                      conflicts: {},
                      'info-update': {},
                    },
                  },
                  insertedEntityIds: [],
                };
              }
              const fileState = state.files[fileId];
              const inThirtyMinutes = Date.now() + 30 * 60 * 1000;
              fileState.expiresAt = inThirtyMinutes;

              // Entities created from this upload must not resurface as "existing"
              // entries when conflict detection re-runs against the client's data
              const detectionsToProcess = options?.excludeInsertedEntityConflicts
                ? filterInsertedEntityConflicts(detections, fileState.insertedEntityIds ?? [])
                : detections;

              // Update existing detections
              const processedDetectionIds = new Set<string>();
              Object.entries(fileState.categories).forEach(([categoryKey, categoryData]) => {
                const category = categoryKey as EntityCategory;
                Object.entries(categoryData).forEach(([entityType, entityData]) => {
                  Object.keys(entityData).forEach(detectionId => {
                    const newDetection = detectionsToProcess.find(d => d.id === detectionId);
                    if (newDetection) {
                      const existingDetectionState = entityData[detectionId];
                      const updatedDetectionState = getUpdatedDetectionState(
                        existingDetectionState,
                        newDetection,
                        showNonConflictingFields,
                      );
                      fileState.categories[category][entityType][detectionId] =
                        updatedDetectionState;
                      processedDetectionIds.add(detectionId);
                    }
                  });
                });
              });

              // Process new detections
              const newDetections = detectionsToProcess.filter(
                d => !processedDetectionIds.has(d.id),
              );
              newDetections.forEach(detection => {
                const categoriesForDetection = extractCategoriesForDetection(
                  detection,
                  showNonConflictingFields,
                );
                fileState.entityTypeByDetectionId[detection.id] = detection.entityType;
                addToCategories(
                  fileState.categories,
                  categoriesForDetection,
                  detection.entityType,
                  detection.id,
                );
              });

              fileState.countMetadata = recalculateAllCounts(fileState);
            },
            undefined,
            'detected-entities/handleDetectionData',
          );
        },

        setFileId: (fileId: string) => {
          set(
            state => {
              state.fileId = fileId;
            },
            undefined,
            'detected-entities/setFileId',
          );
        },

        clearFileData: (fileId: string, resetIdentifier: string) => {
          set(
            state => {
              if (state.appliedResetIdentifiers.includes(resetIdentifier)) {
                return;
              }

              delete state.files[fileId];
              state.appliedResetIdentifiers = [
                ...state.appliedResetIdentifiers,
                resetIdentifier,
              ].slice(-20);
            },
            undefined,
            'detected-entities/clearFileData',
          );
        },

        saveLocalFieldValue: (
          category: EntityCategory,
          detectionId: Detection['id'],
          fieldName: string,
          value: FieldValue,
        ) => {
          const entityType = selectEntityTypeForDetection(get(), detectionId);
          if (entityType == null) {
            return;
          }
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const categoryData = fileState.categories[category];
              if (categoryData == null) {
                throw new Error('No category data found');
              }
              const entityTypeData = categoryData[entityType];
              if (entityTypeData == null) {
                throw new Error('No entity type data found');
              }
              const detectionData = entityTypeData[detectionId];
              if (detectionData == null) {
                throw new Error('No detection data found');
              }
              detectionData.fieldsByKey[fieldName].value = value;
              detectionData.fieldsByKey[fieldName].isLocallyEdited = true;
            },
            undefined,
            'detected-entities/saveLocalFieldValue',
          );
        },

        selectConflictIndex: (
          category: EntityCategory,
          detectionId: Detection['id'],
          conflictIndex: number,
        ) => {
          const state = get();

          set(
            draft => {
              const fileState = selectCurrentFile(draft);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const entityType = selectEntityTypeForDetection(state, detectionId);
              if (entityType == null) {
                throw new Error('No entity type found');
              }
              const detectionState = fileState.categories[category][entityType][detectionId];
              if (detectionState.selectedConflictIndex === conflictIndex) {
                detectionState.selectedConflictIndex = undefined;
                Object.values(detectionState.fieldsByKey).forEach(field => {
                  field.isSelected = false;
                });
              } else {
                detectionState.selectedConflictIndex = conflictIndex;
                Object.values(detectionState.fieldsByKey).forEach(field => {
                  field.isSelected = true;
                });
              }
            },
            undefined,
            'detected-entities/selectConflictIndex',
          );
        },

        toggleConflictField: (
          category: EntityCategory,
          detectionId: Detection['id'],
          fieldName: string,
        ) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const entityType = selectEntityTypeForDetection(state, detectionId);
              if (entityType == null) {
                throw new Error('No entity type found');
              }
              const detectionState = fileState.categories[category][entityType][detectionId];
              if (detectionState == null) {
                throw new Error('No detection state found');
              }
              const field = detectionState.fieldsByKey[fieldName];
              if (field == null) {
                throw new Error('No field found');
              }

              field.isSelected = !field.isSelected;
            },
            undefined,
            'detected-entities/toggleConflictField',
          );
        },

        setResolved: (
          isResolved: boolean,
          category: EntityCategory,
          detectionId: Detection['id'],
          fieldName?: string,
        ) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const entityType = selectEntityTypeForDetection(state, detectionId);
              if (entityType == null) {
                throw new Error('No entity type found');
              }
              const detectionState = fileState.categories[category][entityType][detectionId];

              if (fieldName != null) {
                const field = detectionState.fieldsByKey[fieldName];
                if (field == null) {
                  return;
                }
                field.isResolved = isResolved;
                const isAllResolved = Object.values(detectionState.fieldsByKey).every(
                  field => field.isResolved,
                );
                detectionState.isResolved = isAllResolved;
              } else {
                detectionState.isResolved = isResolved;
                Object.values(detectionState.fieldsByKey).forEach(field => {
                  field.isResolved = isResolved;
                });
              }

              fileState.countMetadata = recalculateAllCounts(fileState);
            },
            undefined,
            'detected-entities/setResolved',
          );
        },

        setExistingValues: (
          category: EntityCategory,
          detectionId: Detection['id'],
          values: EntityFields,
        ) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const detection = selectDetection(state, category, detectionId);
              if (detection == null) {
                throw new Error('No detection found');
              }

              let conflict = null;
              if (detection.conflicts.length === 1) {
                conflict = detection.conflicts[0];
              } else if (values.id != null) {
                conflict = detection.conflicts.find(
                  c => c.conflictingEntityFields.id === values.id,
                );
              }

              if (conflict == null) {
                return;
              }

              conflict.conflictingEntityFields = {
                ...conflict.conflictingEntityFields,
                ...values,
              };
            },
            undefined,
            'detected-entities/setExistingValues',
          );
        },

        setLastAction: (
          category: EntityCategory,
          detectionId: Detection['id'],
          action: LastAction | null,
          fieldName?: string,
        ) => {
          set(
            state => {
              const detectionState = selectDetection(state, category, detectionId);
              if (detectionState == null) {
                throw new Error('No detection state found');
              }

              validateNewLastAction(detectionState, action, detectionId, fieldName);

              if (
                (action === null || action?.type === 'update') &&
                detectionState.lastAction?.type === 'update' &&
                fieldName
              ) {
                if (action === null) {
                  delete detectionState.lastAction.previousValues[fieldName];
                  if (Object.keys(detectionState.lastAction.previousValues).length === 0) {
                    detectionState.lastAction = null;
                  }
                } else {
                  detectionState.lastAction.previousValues = {
                    ...detectionState.lastAction.previousValues,
                    [fieldName]: action.previousValues[fieldName],
                  };
                }
              } else {
                detectionState.lastAction = action;
              }

              const fileState = selectCurrentFile(state);
              if (fileState != null) {
                if (action?.type === 'insert') {
                  fileState.insertedEntityIds ??= [];
                  if (!fileState.insertedEntityIds.includes(action.insertedEntityId)) {
                    fileState.insertedEntityIds.push(action.insertedEntityId);
                  }
                }
                fileState.countMetadata = recalculateAllCounts(fileState);
              }
            },
            undefined,
            'detected-entities/setLastAction',
          );
        },

        setEntityOfTypeInsertLastAction: (
          category: EntityCategory,
          entityType: string,
          insertedIds: {
            [detectionId: string]: string;
          },
        ) => {
          const state = get();
          const fileState = selectCurrentFile(state);
          if (fileState == null) {
            throw new Error('No file state found');
          }

          const categoryData: CategoryData = selectCategory(state, category);
          const detectionIds = Object.keys(categoryData[entityType] || {});

          detectionIds.forEach(detectionId => {
            const detection = selectDetection(state, category, detectionId);
            if (!detection) return;

            if (detection.isConflictable) {
              const lastAction: InsertAction = {
                type: 'insert',
                targetEntityFieldsMetadata: detection.metadata,
                targetEntityType: detection.entityType,
                insertedEntityId: insertedIds[detectionId],
              };
              state.setLastAction(category, detectionId, lastAction);
            } else {
              const nonNullFields = Object.keys(detection.fieldsByKey).filter(
                f => detection.fieldsByKey[f].value != null,
              );
              const previousValues =
                category === 'new'
                  ? nonNullFields.reduce(
                      (acc, fieldName: string) => ({ ...acc, [fieldName]: null }),
                      {},
                    )
                  : detection.conflicts?.[0]?.conflictingEntityFields || {};

              const lastAction: UpdateAction = {
                type: 'update',
                targetEntityFieldsMetadata: detection.metadata,
                targetEntityType: detection.entityType,
                previousValues,
              };
              state.setLastAction(category, detectionId, lastAction);
            }
          });
        },

        setCategoryInsertLastActions: (
          category: EntityCategory,
          insertedIds: {
            [detectionId: string]: string;
          },
        ) => {
          const state = get();
          const fileState = selectCurrentFile(state);
          if (fileState == null) {
            throw new Error('No file state found');
          }

          const categoryData: CategoryData = selectCategory(state, category);
          const allDetectionIds = Object.values(categoryData).flatMap(entityData =>
            Object.keys(entityData),
          );

          allDetectionIds.forEach(detectionId => {
            const detection = selectDetection(state, category, detectionId);
            if (detection == null) {
              return;
            }

            if (detection.isConflictable) {
              const lastAction: InsertAction = {
                type: 'insert',
                targetEntityFieldsMetadata: detection.metadata,
                targetEntityType: detection.entityType,
                insertedEntityId: insertedIds[detectionId],
              };
              state.setLastAction(category, detectionId, lastAction);
            } else {
              const nonNullFields = Object.keys(detection.fieldsByKey).filter(
                f => detection.fieldsByKey[f].value != null,
              );
              const previousValues =
                category === 'new'
                  ? nonNullFields.reduce(
                      (acc, fieldName: string) => ({ ...acc, [fieldName]: null }),
                      {},
                    )
                  : detection.conflicts?.[0]?.conflictingEntityFields || {};

              const lastAction: UpdateAction = {
                type: 'update',
                targetEntityFieldsMetadata: detection.metadata,
                targetEntityType: detection.entityType,
                previousValues,
              };
              state.setLastAction(category, detectionId, lastAction);
            }
          });
        },

        setEntityIdOnDetection: (
          category: EntityCategory,
          detectionId: Detection['id'],
          entityId: string | null,
        ) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const detectionState = selectDetection(state, category, detectionId);
              if (detectionState == null) {
                throw new Error('No detection state found');
              }
              detectionState.entityId = entityId;
            },
            undefined,
            'detected-entities/setEntityIdOnDetection',
          );
        },

        setCategoryResolved: (isResolved: boolean, category: EntityCategory) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const detectionIds = Object.values(fileState.categories[category]).flatMap(
                entityData => Object.keys(entityData),
              );

              detectionIds.forEach(detectionId => {
                const detectionState = selectDetection(state, category, detectionId);
                if (detectionState != null) {
                  detectionState.isResolved = isResolved;
                  Object.values(detectionState.fieldsByKey).forEach(field => {
                    field.isResolved = isResolved;
                  });
                }
              });

              fileState.countMetadata = recalculateAllCounts(fileState);
            },
            undefined,
            'detected-entities/setCategoryResolved',
          );
        },

        setEntitiesOfTypeResolved: (
          isResolved: boolean,
          category: EntityCategory,
          entityType: string,
        ) =>
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                return;
              }
              const detectionIds = Object.keys(fileState.categories[category][entityType] || {});

              detectionIds.forEach(detectionId => {
                const detectionState = selectDetection(state, category, detectionId);
                if (detectionState == null) {
                  return;
                }
                detectionState.isResolved = isResolved;
                Object.values(detectionState.fieldsByKey).forEach(field => {
                  field.isResolved = isResolved;
                });
              });

              fileState.countMetadata = recalculateAllCounts(fileState);
            },
            undefined,
            'detected-entities/setEntitiesOfTypeResolved',
          ),

        setDiscarded: (
          isDiscarded: boolean,
          category: EntityCategory,
          detectionId: Detection['id'],
          options?: {
            fieldNames?: string[];
            skipCountUpdate?: boolean;
          },
        ) => {
          const fieldNames: string[] = [];
          if (Array.isArray(options?.fieldNames)) {
            fieldNames.push(...options.fieldNames);
          }

          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                throw new Error('No file state found');
              }
              const entityType = selectEntityTypeForDetection(state, detectionId);
              if (entityType == null) {
                throw new Error('No entity type found');
              }
              const detectionState = fileState.categories[category][entityType][detectionId];

              if (fieldNames.length === 0) {
                detectionState.isDiscarded = isDiscarded;
              } else {
                fieldNames.forEach(fieldName => {
                  const field = detectionState.fieldsByKey[fieldName];
                  if (field != null) {
                    field.isDiscarded = isDiscarded;
                  }
                });
              }

              if (!options?.skipCountUpdate) {
                fileState.countMetadata = recalculateAllCounts(fileState);
              }
            },
            undefined,
            'detected-entities/setDiscarded',
          );
        },

        discardAll: (category: EntityCategory, entityType: string) => {
          const state = get();
          const detections = selectDetections(state, category, entityType);
          detections.forEach(detection => {
            if (detection.isConflictable) {
              state.setDiscarded(true, category, detection.id, { skipCountUpdate: true });
              return;
            }

            const fieldsToDiscard = Object.keys(detection.fieldsByKey).filter(
              fieldName => detection.fieldsByKey[fieldName].value !== null,
            );

            state.setDiscarded(true, category, detection.id, {
              fieldNames: fieldsToDiscard,
              skipCountUpdate: true,
            });
          });

          set(state => {
            const fileState = selectCurrentFile(state);
            if (fileState != null) {
              fileState.countMetadata = recalculateAllCounts(fileState);
            }
          });
        },

        setActioning: (
          isActioning: boolean,
          category: EntityCategory,
          detectionId: Detection['id'],
          fieldName?: string,
        ) => {
          set(
            state => {
              const detectionState = selectDetection(state, category, detectionId);
              if (detectionState == null) {
                return;
              }

              if (fieldName != null) {
                const field = detectionState.fieldsByKey[fieldName];
                if (field != null) {
                  field.isActioning = isActioning;
                }
              } else {
                detectionState.isActioning = isActioning;
                Object.values(detectionState.fieldsByKey).forEach(field => {
                  field.isActioning = isActioning;
                });
              }
            },
            undefined,
            'detected-entities/setActioning',
          );
        },

        setCategoryActioning: (isActioning: boolean, category: EntityCategory) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                return;
              }
              const categoryData = fileState.categories[category];
              Object.values(categoryData).forEach(entityData => {
                Object.values(entityData).forEach(detection => {
                  if (isActioning && (detection.isResolved || detection.isDiscarded)) {
                    return;
                  }
                  detection.isActioning = isActioning;
                  Object.values(detection.fieldsByKey).forEach(field => {
                    if (isActioning && (field.isResolved || field.isDiscarded)) {
                      return;
                    }
                    field.isActioning = isActioning;
                  });
                });
              });
            },
            undefined,
            'detected-entities/setCategoryActioning',
          );
        },

        setEntitiesOfTypeActioning: (
          isActioning: boolean,
          category: EntityCategory,
          entityType: string,
        ) => {
          set(
            state => {
              const fileState = selectCurrentFile(state);
              if (fileState == null) {
                return;
              }
              const entityData = fileState.categories[category][entityType];
              if (entityData == null) {
                return;
              }
              Object.values(entityData).forEach(detection => {
                if (isActioning && (detection.isResolved || detection.isDiscarded)) {
                  return;
                }
                detection.isActioning = isActioning;
                Object.values(detection.fieldsByKey).forEach(field => {
                  if (isActioning && (field.isResolved || field.isDiscarded)) {
                    return;
                  }
                  field.isActioning = isActioning;
                });
              });
            },
            undefined,
            'detected-entities/setEntitiesOfTypeActioning',
          );
        },
      })),
      {
        name: 'detections-store',
        storage: createJSONStorage(() => idbStorage),
        partialize: state =>
          Object.fromEntries(Object.entries(state).filter(([key]) => !['fileId'].includes(key))),
        onRehydrateStorage: () => rehydratedState => {
          if (rehydratedState == null) {
            return;
          }
          Object.values(rehydratedState.files).forEach(fileState => {
            // States persisted before insertedEntityIds existed need the default
            fileState.insertedEntityIds ??= [];
            Object.values(fileState.categories).forEach(categoryData => {
              Object.values(categoryData).forEach(entityData => {
                Object.values(entityData).forEach(detection => {
                  detection.isActioning = false;
                  Object.values(detection.fieldsByKey).forEach(field => {
                    field.isActioning = false;
                  });
                });
              });
            });
          });
        },
      },
    ),
  ),
);

const selectCurrentFile = (state: State) => {
  const fileId = state.fileId;
  if (fileId == null || state.files == null || state.files[fileId] == null) {
    return null;
  }

  return state.files[fileId];
};

export const selectEntityTypeForDetection = (state: State, detectionId: Detection['id']) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return;
  }
  return fileState.entityTypeByDetectionId[detectionId];
};

const selectCategory = (state: State, category: EntityCategory) => {
  const file = selectCurrentFile(state);
  if (file == null) {
    return {};
  }

  return file.categories[category];
};

const ENTITY_TYPES_IN_ORDER = [
  // Profile
  'PersonalDetails',
  'SoftFacts',
  'Dependant',
  'Health',
  'Vulnerability',
  'EmploymentInfo',
  'Occupation',
  'Objective',
  'EstatePlanning',
  'Beneficiary',
  'RiskAndInvestmentPreferences',
  'TaxProfile',
  'UtilisedBenefit',
  'UtilisedTaxEfficientScheme',
  'UtilisedTaxCredit',
  'ProfessionalConnection',

  // Financial
  // Spending Accounts
  'SpendingAccount',
  'BankAccount',

  // Assets
  'InvestmentAndCash',
  'InvestmentContribution',
  'DefinedContributionPension',
  'DcPensionContribution',
  'DefinedBenefitPension',
  'DbPensionContribution',
  'DefinedBenefitPensionBreakdownItem',
  'PensionAnnuity',
  'IncomeDrawdownPension',
  'IncomeWithdrawal',
  'Property',
  'Collectible',
  'Crypto',
  'FundHolding',

  // Liabilities
  'Loan',
  'Mortgage',
  'MortgageFee',

  // Protection
  'Protection',
  'ProtectionFee',

  // Cashflows
  'Income',
  'Expenditure',
  'LifeEvent',
];
export const selectEntityTypes = (state: State, category: EntityCategory) => {
  const categoryData = selectCategory(state, category);
  const entityTypeKeys = Object.keys(categoryData);
  return entityTypeKeys
    .map(entityType => {
      const detections = selectDetections(state, category, entityType);

      return {
        entityType,
        entityLabel: detections?.[0]?.entityLabel,
      };
    })
    .sort((a, b) => {
      const aIndex = ENTITY_TYPES_IN_ORDER.indexOf(a.entityType);
      const bIndex = ENTITY_TYPES_IN_ORDER.indexOf(b.entityType);
      if (aIndex === -1 && bIndex === -1) {
        return a.entityType.localeCompare(b.entityType);
      }
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    });
};

export const selectDetections = (
  state: State,
  categoryName: EntityCategory,
  entityType?: string,
): DetectionState[] => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return [];
  }
  const detectionsByEntityType = fileState.categories[categoryName];
  if (entityType != null) {
    return Object.values(detectionsByEntityType[entityType]);
  }

  return Object.values(detectionsByEntityType).flatMap(detectionsByEntity =>
    Object.values(detectionsByEntity),
  );
};

export const selectFinalizeStats = (state: State) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return {
      inserts: 0,
      updates: 0,
      unreviewed: 0,
    };
  }

  const { overall } = fileState.countMetadata;
  return {
    inserts: overall.inserts,
    updates: overall.updates,
    unreviewed: overall.total - overall.actioned,
  };
};

export const selectActionedDetectionCount = (state: State) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return 0;
  }
  return fileState.countMetadata.overall.actioned;
};

export const selectTotalDetectionCount = (state: State) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return 0;
  }
  return fileState.countMetadata.overall.total;
};

export const selectDetectionSelector =
  (state: State) =>
  (category: EntityCategory, detectionId: Detection['id']): DetectionState | null => {
    const fileState = selectCurrentFile(state);
    if (fileState == null) {
      throw new Error('No file state found');
    }
    const entityType = selectEntityTypeForDetection(state, detectionId);
    if (entityType == null) {
      throw new Error('No entity type found');
    }
    return fileState.categories[category][entityType][detectionId];
  };

const selectDetection = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
): DetectionState | undefined => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return;
  }
  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return;
  }

  return fileState.categories[category][entityType][detectionId];
};

export const selectSelectedConflictIndex = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
): number | undefined => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return;
  }
  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return;
  }
  const detectionState = fileState.categories[category][entityType][detectionId];
  if (detectionState == null) {
    return;
  }
  return detectionState.selectedConflictIndex;
};

export const selectSelectedFieldNames = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return [];
  }
  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return [];
  }
  const detectionState = fileState.categories[category][entityType][detectionId];
  if (detectionState == null) {
    return [];
  }
  return Object.values(detectionState.fieldsByKey)
    .filter(field => field.isSelected)
    .map(field => field.key);
};

type MissingFieldOptions = {
  fieldNamesToCheck?: string[];
};
function getMissingFieldNames(detection: DetectionState, options: MissingFieldOptions = {}) {
  const missing = [];
  const metadata = detection.metadata;
  const fieldNames =
    options.fieldNamesToCheck ?? Object.keys(metadata).filter(key => !fieldsToHide.includes(key));
  for (const fieldName of fieldNames) {
    const fieldMetadata = metadata[fieldName];
    const fieldValue = detection.fieldsByKey[fieldName]?.value;
    const isFiltered =
      detection.entityType === entityFieldsToFilterOut.entityType &&
      entityFieldsToFilterOut.fieldNames.includes(fieldName);
    if (isEmpty(fieldValue) && !fieldMetadata.isOptional && !isFiltered) {
      missing.push(fieldName);
    }
  }
  return missing;
}

export const selectCanUpdate = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
): {
  canUpdate: boolean;
  reason?: string;
} => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return { canUpdate: false, reason: 'Nothing to update' };
  }
  const detection = selectDetection(state, category, detectionId);

  if (detection == null) {
    return { canUpdate: false, reason: 'Nothing to update' };
  }

  if (category !== 'conflicts') {
    const insertable = selectCanInsert(state, category, detectionId, fieldName);
    return { canUpdate: insertable.canInsert, reason: insertable.reason };
  }

  if (detection.selectedConflictIndex == null) {
    return { canUpdate: false, reason: 'No conflict selected to be updated' };
  }

  const selectedConflict = detection.conflicts[detection.selectedConflictIndex];
  if (selectedConflict == null) {
    return { canUpdate: false, reason: 'No conflict selected to be updated' };
  }
  const selectedFields = selectSelectedFieldNames(state, category, detectionId);

  if (selectedFields.length < 1) {
    return {
      canUpdate: false,
      reason: 'No field selected to be updated',
    };
  }

  const missingFieldNames = getMissingFieldNames(detection, {
    fieldNamesToCheck: fieldName != null ? [fieldName] : selectedFields,
  });

  if (missingFieldNames.length > 0) {
    const readableFieldNames = missingFieldNames.map(fieldName =>
      camelCaseOrSnakeCaseToWords(detection.metadata[fieldName].label ?? fieldName),
    );
    return { canUpdate: false, reason: `${readableFieldNames[0]} is required but has no value.` };
  }

  return { canUpdate: true };
};

export const selectCanInsert = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
): {
  canInsert: boolean;
  reason?: string;
} => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return { canInsert: false, reason: 'Nothing to add' };
  }
  const detection = selectDetection(state, category, detectionId);

  if (detection == null) {
    return { canInsert: false, reason: 'Nothing to add' };
  }

  const hasExistingAction = detection.lastAction !== null;
  const canMergeFieldUpdate = fieldName && detection.lastAction?.type === 'update';
  if (hasExistingAction && !canMergeFieldUpdate) {
    return { canInsert: false, reason: 'Action already in progress' };
  }

  const missingFieldNames = getMissingFieldNames(detection, {
    fieldNamesToCheck: fieldName != null ? [fieldName] : undefined,
  });
  if (missingFieldNames.length > 0) {
    const readableFieldNames = missingFieldNames.map(fieldName =>
      camelCaseOrSnakeCaseToWords(detection.metadata[fieldName].label ?? fieldName),
    );
    return { canInsert: false, reason: `${readableFieldNames[0]} is required but has no value.` };
  }
  return { canInsert: true };
};

export function selectValidationErrorsInCategory(
  state: State,
  category: EntityCategory,
  entityType?: string,
): { entityType: string; reason: string }[] {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return [];
  }

  return selectDetections(state, category, entityType)
    .filter(
      d => !selectIsResolved(state, category, d.id) && !selectIsDiscarded(state, category, d.id),
    )
    .map(detection => {
      const validate = category === 'info-update' ? selectCanUpdate : selectCanInsert;
      const { reason } = validate(state, category, detection.id);
      if (!reason) return null;
      return { entityType: detection.entityType, reason };
    })
    .filter(issue => issue != null);
}

export const selectNewFields = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
): Field[] => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return [];
  }

  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return [];
  }

  const categoryData = fileState.categories[category];
  const detectionData = categoryData[entityType][detectionId];
  return Object.values(detectionData.fieldsByKey);
};

const selectConflict = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  conflictIndex?: number,
) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return null;
  }
  const detection = selectDetection(state, category, detectionId);
  if (detection == null) {
    return null;
  }

  if (conflictIndex != null) {
    return detection.conflicts[conflictIndex];
  }
  if (detection.selectedConflictIndex != null) {
    return detection.conflicts[detection.selectedConflictIndex];
  }

  if (detection.conflicts.length === 1) {
    return detection.conflicts[0];
  }

  return null;
};

export const selectExistingFields = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  conflictIndex?: number,
): Field[] => {
  const detection = selectDetection(state, category, detectionId);
  if (detection == null) {
    return [];
  }

  const conflict = selectConflict(state, category, detectionId, conflictIndex);
  const fieldData: Record<string, FieldValue> = conflict?.conflictingEntityFields ?? {};
  const newFields = selectNewFields(state, category, detectionId);

  return newFields.map(field => {
    return {
      ...field,
      value: fieldData[field.key],
    };
  });
};

type ExistingFieldForConflict = Omit<Field, 'metadata'> & {
  metadata: FieldTypeMetadata | undefined;
  isDetected: boolean;
};

export const selectExistingFieldsForConflict = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  conflictIndex?: number,
): ExistingFieldForConflict[] => {
  const detection = selectDetection(state, category, detectionId);
  if (detection == null) {
    return [];
  }

  const conflict = selectConflict(state, category, detectionId, conflictIndex);
  if (conflict == null) {
    return [];
  }

  const conflictingFields: Record<string, FieldValue> = conflict.conflictingEntityFields;
  const newFields = selectNewFields(state, category, detectionId);
  const detectedKeys = new Set(newFields.map(field => field.key));

  const detectedRows: ExistingFieldForConflict[] = newFields.map(field => ({
    ...field,
    value: conflictingFields[field.key],
    isDetected: true,
  }));

  const extraRows: ExistingFieldForConflict[] = Object.entries(conflictingFields)
    .filter(([key]) => !detectedKeys.has(key) && key !== 'id' && !fieldsToHide.includes(key))
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => ({
      key,
      value,
      metadata: detection.metadata[key],
      isReadOnly: true,
      isSelected: false,
      isResolved: false,
      isDiscarded: false,
      isActioning: false,
      isDetected: false,
    }));

  return [...detectedRows, ...extraRows];
};

export const selectConflictFields = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  conflictIndex?: number,
) => {
  const detection = selectDetection(state, category, detectionId);
  if (detection == null) {
    return [];
  }
  const conflict = selectConflict(state, category, detectionId, conflictIndex);
  if (conflict == null) {
    return [];
  }
  return Object.entries(conflict.conflictingEntityFields)
    .map(([fieldName, value]) => {
      return {
        key: fieldName,
        value,
        metadata: detection.metadata[fieldName],
      };
    })
    .filter(({ value }) => value != null && value !== '');
};

export const selectIsResolved = (
  state: State,
  category?: EntityCategory,
  detectionId?: Detection['id'],
  fieldName?: string,
) => {
  if (detectionId == null || category == null) {
    return false;
  }

  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return false;
  }

  const categoryData = selectCategory(state, category);
  const detectionState = categoryData[entityType][detectionId];
  if (detectionState.isResolved) {
    return true;
  }

  if (fieldName != null) {
    const field = detectionState.fieldsByKey[fieldName];
    if (field == null) {
      return false;
    }
    return field.isResolved;
  }

  return false;
};

export const selectIsDiscarded = (
  state: State,
  category?: EntityCategory,
  detectionId?: Detection['id'],
  fieldName?: string,
) => {
  if (detectionId == null || category == null) {
    return false;
  }

  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return false;
  }

  const categoryData = selectCategory(state, category);
  const detectionState = categoryData[entityType][detectionId];
  if (detectionState.isDiscarded) {
    return true;
  }

  if (fieldName != null) {
    const field = detectionState.fieldsByKey[fieldName];
    if (field == null) {
      return false;
    }
    return field.isDiscarded;
  }

  return false;
};

export const selectIsConflictable = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
): boolean => {
  const detection = selectDetection(state, category, detectionId);
  return detection?.isConflictable ?? false;
};

export const selectIsActioning = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
): boolean => {
  if (detectionId == null || category == null) {
    return false;
  }

  const entityType = selectEntityTypeForDetection(state, detectionId);
  if (entityType == null) {
    return false;
  }

  const categoryData = selectCategory(state, category);
  const detectionState = categoryData[entityType]?.[detectionId];
  if (detectionState == null) {
    return false;
  }

  if (detectionState.isActioning) {
    return true;
  }

  if (fieldName != null) {
    const field = detectionState.fieldsByKey[fieldName];
    if (field == null) {
      return false;
    }
    return field.isActioning;
  }

  return false;
};

export const selectHasActioning = (
  state: State,
  category: EntityCategory,
  entityType?: string,
): boolean => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return false;
  }

  const categoryData = fileState.categories[category];
  const entityTypes = entityType ? [entityType] : Object.keys(categoryData);

  for (const et of entityTypes) {
    const entityData = categoryData[et];
    if (entityData == null) {
      continue;
    }
    for (const detection of Object.values(entityData)) {
      if (detection.isActioning) {
        return true;
      }
      for (const field of Object.values(detection.fieldsByKey)) {
        if (field.isActioning) {
          return true;
        }
      }
    }
  }

  return false;
};

const selectDiscardedFields = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
) => {
  const detectionState = selectDetection(state, category, detectionId);
  if (detectionState == null) {
    return [];
  }
  return Object.values(detectionState.fieldsByKey)
    .filter(field => field.isDiscarded)
    .map(field => field.key);
};

// TODO: deprecate this in favor of selectPayloadForNewDetection
const selectNewPayloadForDetection = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
): UpsertEntityInput[] => {
  const newFields = selectNewFields(state, category, detectionId);
  const detection = selectDetection(state, category, detectionId);
  if (!detection) {
    return [];
  }

  const values = newFields.reduce(
    (acc, field) => {
      acc[field.key] = field.value;
      return acc;
    },
    {} as Record<string, FieldValue>,
  );

  return [
    {
      entityFieldsMetadata: detection.metadata,
      entityType: detection.entityType,
      values,
    },
  ];
};

export const getPayloadForNewDetectionSelector =
  (state: State) =>
  (
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
    valuesOverride?: FieldValue,
  ): UpsertEntityInput | null => {
    const detection = selectDetection(state, category, detectionId);
    if (!detection) {
      return null;
    }

    const discardedFieldNames = selectDiscardedFields(state, category, detectionId);

    const getValues = () => {
      const newFields = selectNewFields(state, category, detectionId).filter(
        f => !discardedFieldNames.includes(f.key),
      );
      if (fieldName) {
        const field = newFields.find(f => f.key === fieldName);
        return field
          ? { [fieldName]: valuesOverride !== undefined ? valuesOverride : field.value }
          : {};
      }

      return newFields.reduce(
        (acc, field) => {
          const value = valuesOverride !== undefined ? valuesOverride : field.value;
          acc[field.key] = getPayloadValue(value, field.metadata);
          return acc;
        },
        {} as Record<string, FieldValue>,
      );
    };

    return {
      entityFieldsMetadata: detection.metadata,
      entityType: detection.entityType,
      values: getValues(),
    };
  };

export const selectNewPayloadForCategory = (
  state: State,
  category: EntityCategory,
): UpsertEntityInput[] => {
  const categoryData = selectCategory(state, category);
  const allDetectionIds = Object.values(categoryData).flatMap(entityData =>
    Object.keys(entityData),
  );

  return allDetectionIds
    .filter(detectionId => {
      const isDiscarded = selectIsDiscarded(state, category, detectionId);
      const isResolved = selectIsResolved(state, category, detectionId);
      return !isDiscarded && !isResolved;
    })
    .map(detectionId => selectNewPayloadForDetection(state, category, detectionId))
    .flat();
};

export const selectDetectionPayloadsForCategory = (
  state: State,
  category: EntityCategory,
): { [p: string]: UpsertEntityInput | null } => {
  const categoryData = selectCategory(state, category);
  const allDetectionIds = Object.values(categoryData).flatMap(entityData =>
    Object.keys(entityData),
  );

  return allDetectionIds
    .filter(
      detectionId =>
        !selectIsDiscarded(state, category, detectionId) &&
        !selectIsResolved(state, category, detectionId),
    )
    .reduce(
      (acc, detectionId) => ({
        ...acc,
        [detectionId]: getPayloadForNewDetectionSelector(state)(category, detectionId),
      }),
      {},
    );
};

export const selectDetectionPayloadsForEntityType = (
  state: State,
  category: EntityCategory,
  entityType: string,
): { [p: string]: UpsertEntityInput | null } => {
  const detectionsByEntityType = selectDetections(state, category, entityType);

  const allDetectionIds = detectionsByEntityType.map(d => d.id);

  return allDetectionIds
    .filter(
      detectionId =>
        !selectIsDiscarded(state, category, detectionId) &&
        !selectIsResolved(state, category, detectionId),
    )
    .reduce(
      (acc, detectionId) => ({
        ...acc,
        [detectionId]: getPayloadForNewDetectionSelector(state)(category, detectionId),
      }),
      {},
    );
};

export const getDetectionsByCategorySelector =
  (state: State) =>
  (category: EntityCategory, entityType?: string): DetectionState[] => {
    const categoryData = selectCategory(state, category);

    return entityType
      ? Object.values(categoryData[entityType] || {})
      : Object.values(categoryData).flatMap(entityData => Object.values(entityData));
  };

export const getPayloadForUpdateDetectionSelector =
  (state: State) =>
  (
    category: EntityCategory,
    detectionId: Detection['id'],
    fieldName?: string,
  ): UpsertEntityInput | null => {
    const detection = selectDetection(state, category, detectionId);
    if (!detection) return null;

    const conflict = selectConflict(state, category, detectionId);
    const newFields = selectNewFields(state, category, detectionId);
    const selectedFieldNames = selectSelectedFieldNames(state, category, detectionId);
    const discardedFieldNames = selectDiscardedFields(state, category, detectionId);
    const onlySelectedFields = detection.isConflictable && detection.conflicts.length > 0;

    const updateFields = onlySelectedFields
      ? newFields.filter(f => selectedFieldNames.includes(f.key))
      : newFields;

    const newPayload = updateFields
      .filter(f => !fieldName || f.key === fieldName)
      .filter(f => !discardedFieldNames.includes(f.key))
      .reduce(
        (acc, field) => {
          acc[field.key] = getPayloadValue(field.value, field.metadata);
          return acc;
        },
        {} as Record<string, FieldValue>,
      );

    const existingFields: Record<string, FieldValue> =
      selectedFieldNames.length > 0
        ? selectExistingFields(state, category, detectionId).reduce(
            (acc, field) => {
              acc[field.key] = field.value;
              return acc;
            },
            {} as Record<string, FieldValue>,
          )
        : {};

    return {
      entityFieldsMetadata: detection.metadata,
      entityType: detection.entityType,
      values: {
        id: conflict?.id,
        ...existingFields,
        ...newPayload,
      },
    };
  };

const getPayloadValue = (value: FieldValue, metadata: FieldTypeMetadata) => {
  if (metadata.type === 'BOOLEAN') {
    return Boolean(value);
  }

  if (metadata.type === 'SELECT') {
    const option = metadata.options.find(o => {
      if (typeof value === 'string' && typeof o.value === 'string') {
        return o.value.toLowerCase() === value.toLowerCase();
      }

      return o.value === value;
    });

    if (option) {
      return option.value;
    }
  }

  return value;
};

export const selectActionDescription = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
) => {
  const isDiscarded = selectIsDiscarded(state, category, detectionId, fieldName);
  if (isDiscarded) {
    return 'Discarded';
  }

  const detectionState = selectDetection(state, category, detectionId);
  const selectedCount = selectSelectedFieldNames(state, category, detectionId).length;
  const fields = selectExistingFields(state, category, detectionId);
  const totalFieldCount = fields.length;
  const allSelected = selectedCount === totalFieldCount;
  const lastAction = detectionState?.lastAction;
  if (lastAction == null) {
    return null;
  }

  if (lastAction.type === 'insert' || (lastAction.type === 'update' && category === 'new')) {
    if (selectedCount > 0 && !allSelected) {
      return `${selectedCount} added`;
    }
    return 'Added';
  }

  if (lastAction.type === 'update') {
    if (selectedCount > 0 && !allSelected) {
      return `${selectedCount} updated`;
    }
    return 'Updated';
  }

  return null;
};

export const selectChanges = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
) => {
  const detectionState = selectDetection(state, category, detectionId);
  if (detectionState == null) {
    return [];
  }

  const isResolved = selectIsResolved(state, category, detectionId, fieldName);
  if (!isResolved) {
    return [];
  }

  const lastAction = detectionState?.lastAction;
  if (lastAction == null) {
    return [];
  }

  const newFields = selectNewFields(state, category, detectionId);
  const previousValues = lastAction.type === 'update' ? lastAction.previousValues : {};

  const result = newFields
    .filter(field => {
      if (fieldName != null) {
        return fieldName === field.key;
      }
      return field.isSelected || lastAction.type === 'insert';
    })
    .map(field => {
      return {
        ...field,
        previousValue: previousValues[field.key],
      };
    });

  return result;
};

export const selectLastAction = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
) => {
  const detectionState = selectDetection(state, category, detectionId);
  return detectionState?.lastAction;
};

export const selectChange = (
  state: State,
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName: string,
) => {
  const changes = selectChanges(state, category, detectionId, fieldName);
  if (changes.length === 0) {
    return null;
  }
  return changes[0];
};

type StateCounts = {
  total: number;
  active: number;
  resolved: number;
  discarded: number;
};

export const selectCategoryStateCounts = (
  state: State,
  category: EntityCategory,
  entityType?: string,
): StateCounts => {
  const fileState = selectCurrentFile(state);

  if (fileState == null) {
    return {
      total: 0,
      active: 0,
      resolved: 0,
      discarded: 0,
    };
  }

  if (entityType) {
    return (
      fileState.countMetadata.byCategory[category][entityType] ?? {
        total: 0,
        active: 0,
        resolved: 0,
        discarded: 0,
      }
    );
  }

  return Object.values(fileState.countMetadata.byCategory[category]).reduce(
    (acc, counts) => ({
      total: acc.total + counts.total,
      active: acc.active + counts.active,
      resolved: acc.resolved + counts.resolved,
      discarded: acc.discarded + counts.discarded,
    }),
    { total: 0, active: 0, resolved: 0, discarded: 0 },
  );
};

const validateNewLastAction = (
  detectionState: DetectionState,
  action: LastAction | null,
  detectionId: Detection['id'],
  fieldName?: string,
) => {
  const loggingData = {
    detectionId,
    fieldName,
    action,
    currentLastAction: detectionState?.lastAction ?? null,
    callStack: new Error().stack,
  };

  if (!detectionState) {
    const error = new Error(`BUG! No detection state found for detectionId: ${detectionId}`);
    Logger.error('BUG! No detection found', error, loggingData);
    throw error;
  }

  if (action === null && detectionState.lastAction === null) {
    Logger.warn(
      'Unexpected attempt to perform null action on detection with null last action',
      loggingData,
    );
  }

  const isLegitimateFieldUpdate =
    action?.type === 'update' && detectionState.lastAction?.type === 'update' && fieldName != null;

  if (action !== null && detectionState.lastAction !== null && !isLegitimateFieldUpdate) {
    Logger.warn('Unexpected attempt to perform action on detection with last action', {
      ...loggingData,
      lastAction: detectionState.lastAction,
    });
  }

  if (fieldName && action?.type === 'insert') {
    const error = new Error('BUG! Cannot insert an individual field');
    Logger.error('BUG! Cannot insert an individual field', error, {
      detectionId,
      fieldName,
      action,
    });
    throw error;
  }
};

export const selectCanClearFileData = (state: State, resetIdentifier?: string) => {
  if (resetIdentifier == null) {
    return false;
  }
  return !state.appliedResetIdentifiers.includes(resetIdentifier);
};

export const selectIsExpired = (state: State) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return false;
  }
  return fileState.expiresAt < Date.now();
};

export const selectVisibleCategories = (state: State) => {
  const fileState = selectCurrentFile(state);
  if (fileState == null) {
    return [];
  }
  const categories: EntityCategory[] = ['new', 'info-update', 'conflicts'];
  const totalNumDetections = selectTotalDetectionCount(state);
  if (totalNumDetections === 0) {
    return categories;
  }
  return categories.filter(category => {
    const categoryData = fileState.categories[category];
    const detectionIds = Object.values(categoryData).flatMap(entityData => Object.keys(entityData));
    return detectionIds.length > 0;
  });
};

export const selectEntityLabel = (state: State, category: EntityCategory, entityType: string) => {
  const detections = selectDetections(state, category, entityType);
  return detections?.[0]?.entityLabel;
};

export const selectExistingColumnVisible = (
  state: State,
  category: EntityCategory,
  entityType?: string,
): boolean => {
  if (category !== 'new') {
    return true;
  }

  const stateCounts = selectCategoryStateCounts(state, category, entityType);
  const hasResolvedEntities = stateCounts.resolved > 0;
  return hasResolvedEntities;
};

export const getLabelId = (
  category: EntityCategory,
  detectionId: Detection['id'],
  fieldName?: string,
) => {
  return fieldName ? `${category}-${detectionId}-${fieldName}` : `${category}-${detectionId}`;
};
